import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { initUser, updateUser } from 'reducers/userReducer';
import { fetchthRm } from 'reducers/partnerFormReducer';
import { fetchUserAuthorities } from 'services/authorityService';
import userService from 'services/userService';
import { GlobalState } from 'reducers/stateTypes';
import { apacCountryCodes, User } from 'types/User';
import { Employee } from 'types/Partner';
import { USER_DATA_LOCAL_STORAGE_KEY } from 'utils/constants';
import { getFromLocalStorage, setInLocalStorage } from 'utils/storageUtils';

export function useUserBootstrap() {
  const dispatch = useDispatch();

  const user = useSelector((state: GlobalState) => state.user.userDetails);
  const thRm = useSelector((state: GlobalState) => state.partnerForm.thRm);
  const storedUserData = getFromLocalStorage<User>(USER_DATA_LOCAL_STORAGE_KEY);

  const isUserConsideredAsApac = (currentRm?: Employee) => {
    const isApac = currentRm
      ? apacCountryCodes.some((countryCode) => countryCode === currentRm.assignedBusinessEntityCountryCode)
      : false;
    return user.hasInApprovalTabAuthority || isApac;
  };

  useEffect(() => {
    async function fetchUserData() {
      let user, userBusinessEntityCountryCode;
      try {
        user = await userService.getUser();
      } catch (error) {
        return user;
      }

      const userAuthorities = await fetchUserAuthorities();

      try {
        userBusinessEntityCountryCode = await userService.getUserBusinessEntity();
      } catch (error) {
        userBusinessEntityCountryCode = 0;
      }

      const loggedInUser = new User(
        user.email,
        user.givenName,
        user.sureName,
        user.userLocation,
        user?.username,
        user.roleList,
        user.authorities,
        userAuthorities,
        userBusinessEntityCountryCode,
      );

      dispatch(
        fetchthRm({
          userID: user.username,
          loadAllEmployees: loggedInUser.hasAllPartnersTabAccess || loggedInUser.hasReadAllSpecialistAuthority,
        }),
      );

      dispatch(initUser(loggedInUser));
      setInLocalStorage(USER_DATA_LOCAL_STORAGE_KEY, loggedInUser);
    }

    const setStoredUserData = async () => {
      const userData = storedUserData!;
      dispatch(initUser(userData));
      dispatch(
        fetchthRm({
          userID: userData.username,
          loadAllEmployees: userData.hasAllPartnersTabAccess || userData.hasReadAllSpecialistAuthority,
        }),
      );
    };

    storedUserData?.username ? setStoredUserData() : fetchUserData();
  }, [dispatch]);

  useEffect(() => {
    if (thRm.length > 0 && !storedUserData?.dataProtectionRegulationLevel) {
      const currentRm: Employee | undefined = thRm.find((i) => i.userId === user.username);
      const isApac: boolean = isUserConsideredAsApac(currentRm);
      const updatedUser = { ...user, isApac };
      dispatch(updateUser(updatedUser));
      setInLocalStorage(USER_DATA_LOCAL_STORAGE_KEY, updatedUser);
    }
  }, [thRm, dispatch]);
}
